import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { Priority } from "@/types";
import { AlertCircle, Check, Users } from "lucide-react";
import { getPriorityStatusMeta } from "./priorityCardTokens";

interface PriorityPeopleRowProps {
  priority: Priority;
  maxVisibleAssignees?: number;
  compact?: boolean;
  /** Used inside board-style PriorityCard: no top border, no duplicate blocker callouts (card footer handles it). */
  embedInCard?: boolean;
}

// Deterministic color per user based on their identifier
const AVATAR_COLORS = [
  "bg-blue-500", "bg-purple-500", "bg-teal-500", "bg-orange-400",
  "bg-pink-500", "bg-indigo-500", "bg-cyan-500", "bg-amber-500",
  "bg-emerald-500", "bg-rose-500",
];

const getUserAvatarColor = (identifier: string): string => {
  let hash = 0;
  for (let i = 0; i < identifier.length; i++) {
    hash = identifier.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

// Returns tailwind bg class for the status dot
const getStatusDotColor = (status?: string | null): string => {
  switch (status) {
    case "focus":   return "bg-violet-500";
    case "done":    return "bg-emerald-500";
    case "blocked": return "bg-red-500";
    case "on-hold": return "bg-amber-500";
    case "waiting": return "bg-cyan-500";
    case "queue":
    default:        return "bg-slate-400";
  }
};

export const PriorityPeopleRow = ({
  priority,
  maxVisibleAssignees = 3,
  compact = false,
  embedInCard = false,
}: PriorityPeopleRowProps) => {
  const assignments = priority.assignments ?? [];
  const visibleAssignees = assignments.slice(0, maxVisibleAssignees);
  const hiddenAssignees = assignments.slice(maxVisibleAssignees);
  const driAssignment = assignments.find((assignment) => assignment.isDri);
  const blockedAssignments = assignments.filter((assignment) => assignment.blocker);

  return (
    <div
      className={cn(
        "pb-4 pt-3",
        !embedInCard && "border-t border-border",
        !embedInCard && priority.status === "blocked" && "border-red-200",
        compact && "pb-3 pt-2.5",
        embedInCard && "pb-3 pt-3",
      )}
    >
      <div className="flex flex-wrap items-center gap-2.5">
        {assignments.length > 0 ? (
          visibleAssignees.map((assignment, index) => {
            const statusMeta = getPriorityStatusMeta(assignment.status);
            const avatarColor = getUserAvatarColor(assignment.assignedUser.pid ?? assignment.assignedUser.fullName);
            const statusDotColor = getStatusDotColor(assignment.status);
            const isDone = assignment.status === "done";
            const isBlocked = assignment.status === "blocked";
            const isWaiting = assignment.status === "waiting";
            const isOnHold = assignment.status === "on-hold";
            const isQueue = assignment.status === "queue";

            return (
              <Tooltip key={`${priority.pid}-assign-${index}`}>
                <TooltipTrigger asChild>
                  <div
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-xs leading-none",
                      "border-border bg-muted/50",
                      assignment.isDri && "border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30",
                      isBlocked && "border-red-200 bg-red-50",
                      isWaiting && "border-cyan-200 bg-cyan-50 dark:border-cyan-800 dark:bg-cyan-950/30",
                      isOnHold && "border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30",
                      isQueue && "border-violet-200 bg-violet-50"
                    )}
                  >
                    {/* Colored avatar */}
                    <div className={cn("h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-semibold text-white overflow-hidden flex-shrink-0", avatarColor)}>
                      {assignment.assignedUser.avatarUrl ? (
                        <img
                          src={assignment.assignedUser.avatarUrl}
                          alt={assignment.assignedUser.fullName}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span>{assignment.assignedUser.fullName.split(" ").map((name) => name[0]).join("").toUpperCase().slice(0, 2)}</span>
                      )}
                    </div>
                    <span
                      className={cn(
                        "max-w-[110px] truncate text-[12px] font-medium text-foreground/80",
                        assignment.isDri && "text-amber-800 dark:text-amber-200",
                        isBlocked && "text-red-700",
                        isQueue && "text-violet-800",
                      )}
                    >
                      {assignment.assignedUser.fullName}
                    </span>
                    {/* Status indicator at right end of chip */}
                    {assignment.isDri ? (
                      <Badge variant="outline" className="h-4 rounded-full border-amber-200 bg-white px-1.5 text-[9px] font-bold uppercase text-amber-700 dark:border-amber-800 dark:bg-transparent dark:text-amber-300">
                        DRI
                      </Badge>
                    ) : null}
                    <span
                      className={cn(
                        "h-3.5 w-3.5 rounded-full flex-shrink-0 flex items-center justify-center",
                        statusDotColor
                      )}
                    >
                      {isDone ? <Check className="h-2 w-2 text-white stroke-[3]" /> : null}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-xs font-medium">{assignment.assignedUser.fullName}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Status: {statusMeta.label}
                    {assignment.icTask ? ` · ${assignment.icTask}` : ""}
                  </p>
                  {statusMeta.tooltip ? (
                    <p className="mt-1 text-xs text-muted-foreground">{statusMeta.tooltip}</p>
                  ) : null}
                  {assignment.blocker ? (
                    <p className="mt-1 text-xs text-red-700">
                      Blocked by {assignment.blocker.fullName}
                      {assignment.blockedReason ? `: ${assignment.blockedReason}` : ""}
                    </p>
                  ) : null}
                </TooltipContent>
              </Tooltip>
            );
          })
        ) : (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Users className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="text-xs">No assignees yet</span>
          </div>
        )}

        {hiddenAssignees.length > 0 ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="inline-flex h-7 min-w-7 items-center justify-center rounded-full border border-primary/30 bg-primary/10 px-2.5 text-xs font-medium text-primary">
                +{hiddenAssignees.length}
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <div className="flex flex-col gap-1.5">
                {hiddenAssignees.map((assignment, hiddenIdx) => (
                  <div key={`${priority.pid}-hidden-${maxVisibleAssignees + hiddenIdx}`}>
                    <span className="text-xs font-medium block">{assignment.assignedUser.fullName}</span>
                    <span className="text-xs text-muted-foreground">
                      {getPriorityStatusMeta(assignment.status).label}
                      {assignment.icTask ? ` · ${assignment.icTask}` : ""}
                    </span>
                  </div>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>

      {!embedInCard && blockedAssignments.length > 0 ? (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2">
          <div className="flex items-center gap-1.5 text-xs font-medium text-red-700">
            <AlertCircle className="h-3.5 w-3.5" />
            Assignee blockers
          </div>
          <div className="mt-1.5 space-y-1">
            {blockedAssignments.slice(0, 2).map((assignment, blockIdx) => (
              <p key={`${priority.pid}-block-${blockIdx}`} className="truncate text-xs text-red-700">
                {assignment.assignedUser.fullName} blocked by {assignment.blocker?.fullName}
                {assignment.blockedReason ? `: ${assignment.blockedReason}` : ""}
              </p>
            ))}
            {blockedAssignments.length > 2 ? (
              <p className="text-xs text-red-700">+{blockedAssignments.length - 2} more blockers</p>
            ) : null}
          </div>
        </div>
      ) : !embedInCard && priority.blocker ? (
        <div className="mt-3 flex min-w-0 items-center justify-between gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2">
          <span className="truncate text-xs text-red-700">
            {priority.blocker.fullName} blocked this priority
            {driAssignment?.assignedUser?.fullName ? ` — ${driAssignment.assignedUser.fullName} needs to resolve.` : "."}
          </span>
          <span className="whitespace-nowrap text-[11px] font-medium text-red-700">
            Notify DRI
          </span>
        </div>
      ) : null}
    </div>
  );
};
